import * as React from 'react'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import debounce from 'lodash/debounce'
import { AuthContext } from 'Context'
import { SearchInput } from 'Components/input'
import { checkCircleBody, Column, DataTable, dateBody, ToolPanel } from 'Components/readonly'
import { preserveNewlinesColumnBody } from 'Utils/newLines'
import useCrudData from 'Hooks/useCrudData'
import SdsStatus from 'Pages/sds/components/sdsStatus'
import SdsDetail from 'Pages/sds/sdsDetail'
import SdsPublicSwitch from 'Pages/sds/components/sdsPublicSwitch'

const RevisionSubscriptionCompletedTable = ({ admin }) => {
  const intl = useIntl()
  const [showDetailId, setShowDetailId] = useState(null)
  const [first, setFirst] = useState(0)
  const [page, setPage] = useState(1)
  const [searchValue, setSearchValue] = useState("")
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState({ field: 'id', order: -1 })
  const { organisation } = React.useContext(AuthContext)
  const url = '/rapi/' + (admin ? 'admin/' : '') + 'revisionsubscription/listcompletedsds'
  const [isLoading, sds, config, readSds, updateSds, deleteData, setData, meta] = useCrudData(url + '?page=' + page + "&search="+search + "&sort=" + sort.field + "&order=" + sort.order, [], [organisation])
  const showDetailSds = readSds(showDetailId)

  /**
   * Debounced search setter to reduce API calls and reset pagination after 300ms idle.
   * Optimizes performance for dynamic input.
   * @type {DebouncedFunc<(query: string) => void>}
   */
  const debouncedFetch = useMemo(() => debounce((query) => {
    setPage(1);
    setSearch(query);
  }, 300), [setSearch])

  const publicBody = (row) => <SdsPublicSwitch kind="internal" sds={row} updateSds={updateSds}/>

  const columns = [
    <Column
      field="status"
      header={intl.formatMessage({ id: 'common.status' })}
      body={(row) => <SdsStatus sds={row}/>}
      sortable={true}
      style={{ width: '1%', whiteSpace: 'nowrap' }}
    />,
    <Column
      field="binding_from"
      body={dateBody}
      header={intl.formatMessage({ id: 'sds.revision-subscription.binding-from' })}
      sortable={false}
      style={{ width: '1%', textAlign: 'center' }}
    />,
    <Column
      field="productName"
      header={intl.formatMessage({ id: 'chemicals.product' })}
      body={preserveNewlinesColumnBody}
      sortable={true}
    />,
    <Column
      field="id"
      header={intl.formatMessage({ id: 'sds.id' })}
      sortable={true} style={{ width: '110px' }}
    />,
    <Column
      field="countryName"
      header={intl.formatMessage({ id: 'common.country' })}
      sortable={true}
      style={{ width: '1%', whiteSpace: 'nowrap' }}
    />,
    <Column
      field="langName"
      header={intl.formatMessage({ id: 'common.language' })}
      sortable={true}
      style={{ width: '1%', whiteSpace: 'nowrap' }}
    />,
    <Column
      field="has_labeling"
      body={checkCircleBody}
      header={intl.formatMessage({ id: 'labeling.product-labeling' })}
      sortable={false}
      style={{ width: '1%', textAlign: 'center' }}
    />
  ]

  if (organisation.weblink > 0) {
    columns.push(
      <Column
        key="isPublic"
        field="isPublic"
        header={intl.formatMessage({ id: 'sds.public' })}
        sortable={false}
        body={publicBody}
        style={{ width: '1%' }}
      />
    )
  }

  if (admin) {
    columns.unshift(<Column field="organisation.name" header={intl.formatMessage({ id: 'admin.customer' })}
                            sortable={true}/>)
  }

  return (
    <>
      <ToolPanel>
        <SearchInput
          value={searchValue}
          onChange={(event) => {
            debouncedFetch(event.target.value)
            setSearchValue(event.target.value);
            setFirst(0);
          }}
        />
      </ToolPanel>
      <DataTable
        className={'flextable clickable-rows'}
        value={sds}
        paginator={true}
        rows={10}
        first={first}
        setPage={setPage}
        onPage={(e) => {
          setFirst(e.first)
          setPage(e.page + 1)
        }}
        sortField={sort.field}
        sortOrder={sort.order}
        onSort={(e) => {
          setSort({ field: e.sortField, order: e.sortOrder })
        }}
        totalRecords={(meta?.total_items ?? 0)}
        lazy={true}
        loading={isLoading}
        onRowClick={(event) => setShowDetailId(event.data.id)}
        responsive={true}>
        {columns}
      </DataTable>

      <SdsDetail
        sds={showDetailSds}
        kind="internal"
        isOpen={showDetailSds != null}
        close={() => {
          setShowDetailId(null)
        }}
        onSdsUpdated={(value) => {
          updateSds(value)
        }}
        publicBody={publicBody}
      />
    </>
  )
}

export default RevisionSubscriptionCompletedTable
